import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AppState, ChatSession, Message, MCPServer, LLMProvider, AppSettings, SessionTokenUsage } from '@/types';
import { sanitizeImportedSettings } from '@/stores/settingsAllowlist';
import { generateUUID } from '@/utils/uuid';
import { apiClient, TokenUsage, SessionCostResponse, ApiError } from '@/utils/apiClient';
import { mapSettledWithLimit, SERVER_FANOUT_LIMIT } from '@/utils/concurrency';
import { parseProviderHealth } from '@/utils/providerHealth';

/**
 * Docker users often hit a "blank white screen" after upgrades because persisted
 * localStorage data is either corrupted or shape-changed.
 *
 * This wrapper pre-validates JSON and clears the key if it is unreadable, so the app
 * boots instead of crashing during hydration.
 */
const safeLocalStorage: Storage = {
  getItem: (name: string) => {
    try {
      const value = localStorage.getItem(name);
      if (!value) return null;
      try {
        // Validate JSON before zustand parses it (prevents hard crash).
        JSON.parse(value);
        return value;
      } catch {
        localStorage.removeItem(name);
        return null;
      }
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string) => {
    try {
      localStorage.setItem(name, value);
    } catch {
      // ignore
    }
  },
  removeItem: (name: string) => {
    try {
      localStorage.removeItem(name);
    } catch {
      // ignore
    }
  },
  clear: () => {
    try {
      localStorage.clear();
    } catch {
      // ignore
    }
  },
  key: (index: number) => {
    try {
      return localStorage.key(index);
    } catch {
      return null;
    }
  },
  get length() {
    try {
      return localStorage.length;
    } catch {
      return 0;
    }
  },
};

export const defaultSettings: AppSettings = {
  language: 'ru', // Russian by default
  theme: 'system',
  fontSize: 'medium',
  codeTheme: 'vs-dark',
  autoSave: true,
  showTimestamps: true,
  enableNotifications: true,
  llmMode: 'auto', // Default to auto mode
  selectedLLMProvider: undefined,
};

// Stable id = type + model (e.g. "anthropic-claude-sonnet-4") so:
// - the same selection works across browsers/devices
// - several models of one provider can coexist in the UI
const defaultLLMProvider: LLMProvider = {
  id: 'gigachat-GigaChat-Max',
  name: 'GigaChat',
  type: 'gigachat',
  model: 'GigaChat-Max',
  enabled: true,
  settings: {
    maxTokens: 128000,
    temperature: 0.7,
  },
};

/** Map backend provider name (e.g. "ollama", "ollama-qwen") to frontend type. */
function mapBackendProviderType(
  rawType: string
): LLMProvider['type'] {
  if (rawType === 'gigachat') return 'gigachat';
  if (rawType === 'openai') return 'openai';
  if (rawType === 'anthropic') return 'anthropic';
  if (rawType === 'ollama' || rawType.startsWith('ollama-') || rawType.startsWith('ollama_')) {
    return 'ollama';
  }
  return 'custom';
}

/**
 * Build a cross-device-stable provider id.
 * Examples: "gigachat-GigaChat-Max", "anthropic-claude-opus-4", "ollama-qwen2.5:32b"
 */
function makeLLMProviderId(type: string, model: string): string {
  const m = (model || '').trim();
  if (!m) return type;
  // Backend Ollama keys are already "ollama-<model>"
  if (m.startsWith(`${type}-`)) return m;
  return `${type}-${m}`;
}

/** Upgrade legacy ids (type-only / gigachat-default / UUID) → type-model. */
function migrateProviderList(
  providers: LLMProvider[],
  selectedId?: string
): { providers: LLMProvider[]; selectedId?: string } {
  const idMap = new Map<string, string>();
  const migrated = providers.map((p) => {
    if (!p?.type) return p;
    const stable = makeLLMProviderId(p.type, p.model || '');
    if (p.id !== stable) {
      idMap.set(p.id, stable);
      return { ...p, id: stable };
    }
    return p;
  });

  // Same type+model → one row (keep first)
  const seen = new Set<string>();
  const deduped: LLMProvider[] = [];
  for (const p of migrated) {
    if (!p?.id || seen.has(p.id)) continue;
    seen.add(p.id);
    deduped.push(p);
  }

  let sel = selectedId;
  if (sel && idMap.has(sel)) {
    sel = idMap.get(sel);
  }
  return { providers: deduped, selectedId: sel };
}

// Determine default MCP server URL based on environment
// In production (Docker), use relative path through nginx proxy
// In development, use localhost
const getDefaultMCPServerUrl = (): string => {
  const viteEnv = (import.meta as any).env;
  if (viteEnv?.PROD === true || viteEnv?.MODE === 'production') {
    // Relative URL works through nginx proxy
    return '/mcp/sse/';
  }
  // Development: direct connection to local MCP server
  return 'http://127.0.0.1:9000/sse/';
};

const defaultMCPServers: MCPServer[] = [
  {
    id: 'astromkey-server',
    name: 'Astromkey',
    url: getDefaultMCPServerUrl(),
    transport: 'sse',
    enabled: true,
    status: 'disconnected',
  },
];

interface AppStore extends AppState {
  // Session management
  createSession: (title?: string) => void;
  selectSession: (sessionId: string) => void;
  deleteSession: (sessionId: string) => void;
  updateSessionTitle: (sessionId: string, title: string) => void;
  pinSession: (sessionId: string, pinned: boolean) => void;
  reorderSessions: (dragId: string, hoverId: string) => void;
  
  // Server-side session sync
  loadSessionsFromServer: () => Promise<void>;
  
  // Message management. STORY-299: `sessionId` names the chat written to
  // (the one that sent the request); omitted, the open chat is meant. A
  // write to a chat that no longer exists does nothing, locally or on the server.
  // STORY-524: `reply.id` names the message (an answer takes its turn's id),
  // and `reply.persisted` says the server already stored it: no POST.
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>, sessionId?: string, reply?: StoredReply) => void;
  updateMessage: (messageId: string, updates: Partial<Message>, sessionId?: string) => void;
  deleteMessage: (messageId: string, sessionId?: string) => void;
  clearMessages: (sessionId?: string) => void;
  clearMessagesAndHistory: (sessionId?: string) => Promise<void>;  // Also clears server-side conversation history
  addToolEvents: (events: Array<{ name: string; args: Record<string, any>; status?: string; duration_ms?: number | null; timestamp: Date }>, sessionId?: string) => void;

  // Token tracking
  addSessionTokenUsage: (usage: TokenUsage, sessionId?: string) => void;
  /**
   * STORY-290 (R-4): the server's per-session usage (/api/analytics/session/<id>/cost),
   * the ONE source every session token badge reads, so they always agree.
   */
  sessionStats: Record<string, SessionCostResponse>;
  /** Bumped after every answer; per-session counters (context, cost) refetch on it. */
  sessionStatsRevision: number;
  /** Refetch the session's usage once and notify the counters. Never throws. */
  refreshSessionStats: (sessionId: string) => Promise<void>;
  
  // MCP Server management
  addMCPServer: (server: Omit<MCPServer, 'id'>) => void;
  updateMCPServer: (serverId: string, updates: Partial<MCPServer>) => void;
  deleteMCPServer: (serverId: string) => void;
  toggleMCPServer: (serverId: string) => void;
  toggleFavoriteTool: (serverName: string, toolName: string) => void;
  
  // LLM Provider management
  addLLMProvider: (provider: Omit<LLMProvider, 'id'>) => void;
  updateLLMProvider: (providerId: string, updates: Partial<LLMProvider>) => void;
  deleteLLMProvider: (providerId: string) => void;
  setActiveLLMProvider: (providerId: string) => void;
  /** Merge providers initialized on the backend into the UI list (cross-device). */
  syncLLMProvidersFromBackend: () => Promise<void>;
  
  // Settings
  updateSettings: (settings: Partial<AppSettings>) => void;
  loadSettingsFromServer: () => Promise<void>;
  
  // STORY-299: chat turns. A chat is busy while it has a turn in flight;
  // other chats are not. The global isLoading/setLoading stay for any other
  // consumer, but chat code no longer reads or writes them.
  /**
   * Start a turn for `sessionId`. Returns the AbortSignal to pass to the
   * request, or null when that chat already has a turn in flight, or is gone
   * (closed, or the store was reset on logout): refuse the send.
   */
  beginTurn: (sessionId: string, retry?: { attempt: number }, turnId?: string, serverSave?: boolean) => AbortSignal | null;
  /** End the turn that owns `signal` (a no-op for a turn already stopped). */
  endTurn: (sessionId: string, signal: AbortSignal) => void;
  /**
   * Stop the chat's turn: aborts its request; its late reply writes nothing.
   * STORY-511: the server's work is cancelled too. When the server already
   * had the answer, the chat shows it once stored (it was computed and paid for).
   */
  abortTurn: (sessionId: string) => void;

  // UI state
  setLoading: (loading: boolean) => void;
  /**
   * `sessionId` binds the error to one chat (shown only while it is open, see
   * visibleError); a background chat's failure must not paint the open one.
   */
  setError: (error: string | null, sessionId?: string) => void;
  /**
   * STORY-376: the chat a session-bound error belongs to (the not-saved
   * banner); null for a global error. See visibleError.
   */
  errorSessionId: string | null;
  
  // Cross-component refresh triggers
  triggerVaultRefresh: () => void;
}

/** Latest refreshSessionStats request per session (stale-response guard). */
const _sessionStatsRequests = new Map<string, number>();

/** Track which sessions have been fully loaded from server (messages fetched). */
const _loadedSessionIds = new Set<string>();

/**
 * STORY-376: POST /api/sessions for chats created in this tab, by id. The
 * promise settles true once the server has the session, false if the create
 * failed. A confirmed entry is dropped; a failed one stays so later requests
 * know the server never got the session. No entry = the server already has it
 * (listed by it, or confirmed).
 *
 * Every request naming a session id waits on this first. The server answers
 * 404 for an id it has not created (ownership checks stay strict), so before
 * this gate the counters 404'd on most new chats and a message sent before
 * the create answered was refused and lost from the saved history.
 */
const _sessionCreates = new Map<string, Promise<boolean>>();

/**
 * A create that never answers would hold every request for its chat back
 * with no error shown. After this long the create counts as failed (and its
 * fetch is aborted), so the queued message surfaces the not-saved banner.
 *
 * KNOWN LIMITATION (follow-up): a failed create is final for this tab. If the
 * server committed the row but the answer was lost (or arrived after the
 * timeout), later sends for that chat are still refused here. The fix is to
 * re-create on the next send and treat 409 as already-created.
 */
export const SESSION_CREATE_TIMEOUT_MS = 15_000;

/**
 * Bumped by resetAppStore (logout). Work chained on a create from an older
 * generation is dropped: it would otherwise fire after the reset with no
 * token or with the next user's, and could set a banner in their store.
 */
let _storeGeneration = 0;

/**
 * The current store generation. Capture it before waiting on a create and
 * drop the work if it has changed by the time the wait ends (a logout ran).
 */
export function storeGeneration(): number {
  return _storeGeneration;
}

/** Resolves true once the server has the session; false if its create failed. */
export function whenSessionConfirmed(sessionId: string): Promise<boolean> {
  return _sessionCreates.get(sessionId) ?? Promise.resolve(true);
}

/** Resolve false after `ms`, aborting the request, unless `request` settles first. */
function withCreateTimeout(request: Promise<boolean>, controller: AbortController, ms: number): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => {
      console.warn(`Session create did not answer within ${ms} ms; treating it as failed`);
      controller.abort();
      resolve(false);
    }, ms);
  });
  return Promise.race([request, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Fire-and-forget a request for `sessionId` once the server has the session.
 * When its create failed the request is not sent (it could only 404) and
 * `onUnsaved` runs instead, so the caller can say so rather than drop it.
 */
function sendWhenConfirmed(
  sessionId: string,
  send: () => Promise<unknown>,
  failureLog: string,
  onUnsaved?: () => void,
): void {
  const generation = _storeGeneration;
  whenSessionConfirmed(sessionId)
    .then((confirmed) => {
      if (generation !== _storeGeneration) return; // logged out meanwhile
      if (!confirmed) {
        onUnsaved?.();
        return;
      }
      return send();
    })
    .catch((err) => {
      console.warn(failureLog, err);
    });
}

function sessionNotSavedError(language?: string): string {
  return language === 'en'
    ? 'The chat could not be saved on the server: this message was not saved and will be missing after a reload.'
    : 'Не удалось сохранить чат на сервере: сообщение не сохранено и пропадёт после перезагрузки.';
}

/**
 * The error to show for the chat on screen: a global error always, a
 * session-bound one (the not-saved banner) only while its chat is active.
 */
export function visibleError(state: {
  error: string | null;
  errorSessionId: string | null;
  currentSession: { id: string } | null;
}): string | null {
  if (!state.error) return null;
  if (state.errorSessionId && state.errorSessionId !== state.currentSession?.id) return null;
  return state.error;
}

const DEFAULT_SESSION_TITLES = new Set(['New Chat', 'Новый чат']);

function defaultSessionTitle(language?: string): string {
  return language === 'en' ? 'New Chat' : 'Новый чат';
}

function isDefaultSessionTitle(title: string): boolean {
  return DEFAULT_SESSION_TITLES.has(title);
}

/**
 * STORY-299: the request in flight for each chat, by session id. Not in the
 * store (not serialisable); the store holds only pendingBySession.
 */
const _chatControllers = new Map<string, AbortController>();

/** Abort every chat turn in flight (logout, clear-all). */
function abortAllTurns(): void {
  _chatControllers.forEach((controller) => controller.abort());
  _chatControllers.clear();
  stopAllReattachPolls();
}

/** STORY-524: how an answer was stored, for addMessage. */
export interface StoredReply {
  /** The message id: the turn's id, the one the server stored it under. */
  id: string;
  /** True when the server already stored this answer: the client does not POST it. */
  persisted?: boolean;
}

/**
 * STORY-511: ask the server to stop a turn. Never throws, never blocks Stop:
 * answers the status, or null when the request failed (the turn then runs to
 * the end on the server, as every turn did before).
 */
function sendTurnCancel(turnId: string, reason: 'stop' | 'close_chat'): Promise<string | null> {
  return Promise.resolve()
    .then(() => apiClient.cancelTurn(turnId, reason))
    .then((r) => r?.status ?? null)
    .catch((err) => {
      console.warn('Failed to cancel the turn on the server:', err);
      return null;
    });
}

/** How often a reattached turn is polled (and on every return to the tab). */
export const REATTACH_POLL_MS = 3_000;

/** STORY-524: polls of turns found running on the server, by session id. */
const _reattachPolls = new Map<string, { turnId: string; timer: ReturnType<typeof setInterval>; onVisible: () => void }>();

function stopReattachPoll(sessionId: string): void {
  const poll = _reattachPolls.get(sessionId);
  if (!poll) return;
  clearInterval(poll.timer);
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', poll.onVisible);
  _reattachPolls.delete(sessionId);
}

function stopAllReattachPolls(): void {
  Array.from(_reattachPolls.keys()).forEach(stopReattachPoll);
}

type HistoryMessage = { id: string; type: string; content: string; metadata: any; created_at: string };

function messagesFromHistory(messages: HistoryMessage[]): Message[] {
  return messages.map((m) => ({
    id: m.id,
    type: m.type as 'user' | 'assistant' | 'system',
    content: m.content,
    timestamp: new Date(m.created_at),
    metadata: m.metadata || undefined,
  }));
}

/** Abort the turn of a chat that is going away; its reply must write nothing. */
function abortTurnOf(sessionId: string): void {
  _chatControllers.get(sessionId)?.abort();
  _chatControllers.delete(sessionId);
}

function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  if (!(key in record)) return record;
  const { [key]: _dropped, ...rest } = record;
  return rest;
}

type SessionsSlice = { currentSession: ChatSession | null; sessions: ChatSession[] };

/**
 * The chat a write targets: `sessionId`, or the open chat when omitted. The
 * open chat's object is preferred when the ids match (it is the one the
 * pre-STORY-299 writers updated); null when the chat no longer exists.
 */
function targetSession(state: SessionsSlice, sessionId?: string): ChatSession | null {
  const id = sessionId ?? state.currentSession?.id;
  if (!id) return null;
  if (state.currentSession?.id === id) return state.currentSession;
  return state.sessions.find((s) => s.id === id) ?? null;
}

/** Put `updated` in place of its chat, in the list and as the open chat if it is open. */
function replaceSession(state: SessionsSlice, updated: ChatSession): SessionsSlice {
  return {
    currentSession: state.currentSession?.id === updated.id ? updated : state.currentSession,
    sessions: state.sessions.map((s) => (s.id === updated.id ? updated : s)),
  };
}

/**
 * Update one chat by id (STORY-299). The open chat is touched only when it IS
 * that chat. A chat that is gone leaves the state unchanged.
 */
function withSession<S extends SessionsSlice>(
  state: S,
  sessionId: string | undefined,
  update: (session: ChatSession) => ChatSession,
): S | SessionsSlice {
  const target = targetSession(state, sessionId);
  if (!target) return state;
  return replaceSession(state, update(target));
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Initial state
      currentSession: null,
      sessions: [],
      mcpServers: defaultMCPServers,
      llmProviders: [defaultLLMProvider],
      settings: defaultSettings,
      isLoading: false,
      pendingBySession: {},
      sessionsLoading: false,
      historyLoadingIds: [],
      lastActiveSessionId: null,
      error: null,
      errorSessionId: null,
      vaultRefreshTrigger: 0,
      sessionStats: {},
      sessionStatsRevision: 0,

      // ---------------------------------------------------------------
      // Server-side session sync
      // ---------------------------------------------------------------

      loadSessionsFromServer: async () => {
        set({ sessionsLoading: true });
        try {
          const serverSessions = await apiClient.listSessions();
          const sessions: ChatSession[] = serverSessions.map((s) => ({
            id: s.id,
            title: s.title,
            messages: [], // loaded lazily when selected
            messageCount: s.message_count,
            createdAt: new Date(s.created_at),
            updatedAt: new Date(s.updated_at),
            pinned: s.pinned,
          }));
          _loadedSessionIds.clear();
          // Re-open the session that was active before the reload when it
          // still exists; otherwise fall back to the newest one (F-6b).
          const remembered = get().lastActiveSessionId;
          const initial = sessions.find((s) => s.id === remembered) || sessions[0] || null;
          set({
            sessions,
            currentSession: initial,
            lastActiveSessionId: initial?.id ?? null,
            sessionsLoading: false,
          });

          // Load the messages of the session being shown
          if (initial) {
            set((state) => ({ historyLoadingIds: [...state.historyLoadingIds, initial.id] }));
            try {
              const detail = await apiClient.getSession(initial.id);
              const messagesLoaded: Message[] = detail.messages.map((m) => ({
                id: m.id,
                type: m.type as 'user' | 'assistant' | 'system',
                content: m.content,
                timestamp: new Date(m.created_at),
                metadata: m.metadata || undefined,
              }));
              _loadedSessionIds.add(initial.id);
              set((state) => {
                const updated = state.sessions.map((s) =>
                  s.id === initial.id
                    ? { ...s, messages: messagesLoaded, messageCount: messagesLoaded.length }
                    : s
                );
                return {
                  sessions: updated,
                  currentSession: state.currentSession?.id === initial.id
                    ? { ...state.currentSession, messages: messagesLoaded, messageCount: messagesLoaded.length }
                    : state.currentSession,
                };
              });
              // STORY-524: the page was reloaded while this chat waited.
              if (detail.pending_turn) reattachTurn(initial.id, detail.pending_turn);
            } catch (err) {
              console.warn('Failed to load messages for initial session:', err);
            } finally {
              set((state) => ({ historyLoadingIds: state.historyLoadingIds.filter((id) => id !== initial.id) }));
            }
          }
        } catch (err) {
          console.warn('Failed to load sessions from server:', err);
          set({ sessionsLoading: false });
        }
      },

      // Session management
      createSession: (title?: string) => {
        const sessionTitle = title ?? defaultSessionTitle(get().settings.language);
        const sessionId = generateUUID();
        const newSession: ChatSession = {
          id: sessionId,
          title: sessionTitle,
          messages: [],
          messageCount: 0,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        set((state) => ({
          sessions: [newSession, ...state.sessions],
          currentSession: newSession,
          lastActiveSessionId: sessionId,
        }));

        _loadedSessionIds.add(sessionId);

        // Persist to server; every later request for this id waits on it (STORY-376).
        const controller = new AbortController();
        const created = withCreateTimeout(
          apiClient.createSession(sessionId, sessionTitle, controller.signal).then(
            () => true,
            (err) => {
              console.warn('Failed to persist session to server:', err);
              return false;
            },
          ),
          controller,
          SESSION_CREATE_TIMEOUT_MS,
        ).then((confirmed) => {
          if (confirmed && _sessionCreates.get(sessionId) === created) _sessionCreates.delete(sessionId);
          return confirmed;
        });
        _sessionCreates.set(sessionId, created);
      },

      selectSession: (sessionId: string) => {
        const session = get().sessions.find((s) => s.id === sessionId);
        if (session) {
          set({ currentSession: session, lastActiveSessionId: sessionId });

          // Lazy-load messages from server if not yet loaded (and not already in flight)
          if (!_loadedSessionIds.has(sessionId) && !get().historyLoadingIds.includes(sessionId)) {
            set((state) => ({ historyLoadingIds: [...state.historyLoadingIds, sessionId] }));
            apiClient.getSession(sessionId).then((detail) => {
              const messagesLoaded: Message[] = detail.messages.map((m) => ({
                id: m.id,
                type: m.type as 'user' | 'assistant' | 'system',
                content: m.content,
                timestamp: new Date(m.created_at),
                metadata: m.metadata || undefined,
              }));
              _loadedSessionIds.add(sessionId);
              set((state) => {
                const updatedSessions = state.sessions.map((s) =>
                  s.id === sessionId
                    ? { ...s, messages: messagesLoaded, messageCount: messagesLoaded.length }
                    : s
                );
                const updatedCurrent = state.currentSession?.id === sessionId
                  ? { ...state.currentSession, messages: messagesLoaded, messageCount: messagesLoaded.length }
                  : state.currentSession;
                return {
                  sessions: updatedSessions,
                  currentSession: updatedCurrent,
                };
              });
              // STORY-524: a turn of this chat is still being answered on the server.
              if (detail.pending_turn) reattachTurn(sessionId, detail.pending_turn);
            }).catch((err) => {
              console.warn('Failed to load session messages:', err);
              // Mark as loaded so we don't retry endlessly
              _loadedSessionIds.add(sessionId);
            }).finally(() => {
              set((state) => ({ historyLoadingIds: state.historyLoadingIds.filter((id) => id !== sessionId) }));
            });
          }
        }
      },

      deleteSession: (sessionId: string) => {
        // STORY-299: a chat closed while it waits on an answer stops waiting;
        // the late reply has no chat to land in and writes nothing.
        // STORY-511: and its server work stops (DELETE /api/sessions cancels
        // it too, should this request be lost).
        const closingTurnId = get().pendingBySession[sessionId]?.turnId;
        if (closingTurnId) void sendTurnCancel(closingTurnId, 'close_chat');
        stopReattachPoll(sessionId);
        abortTurnOf(sessionId);
        set((state) => {
          const newSessions = state.sessions.filter((s) => s.id !== sessionId);
          const newCurrentSession = state.currentSession?.id === sessionId 
            ? newSessions[0] || null 
            : state.currentSession;
          
          return {
            sessions: newSessions,
            currentSession: newCurrentSession,
            lastActiveSessionId: newCurrentSession?.id ?? null,
            pendingBySession: withoutKey(state.pendingBySession, sessionId),
          };
        });

        _loadedSessionIds.delete(sessionId);

        // Delete on server once it has the session — a DELETE racing the
        // create would 404 and the create would then leave a ghost chat.
        sendWhenConfirmed(
          sessionId,
          () => apiClient.deleteServerSession(sessionId),
          'Failed to delete session on server:',
        );
      },

      updateSessionTitle: (sessionId: string, title: string) => {
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId ? { ...s, title, updatedAt: new Date() } : s
          ),
          currentSession: state.currentSession?.id === sessionId
            ? { ...state.currentSession, title, updatedAt: new Date() }
            : state.currentSession,
        }));

        // Fire-and-forget: update on server
        sendWhenConfirmed(
          sessionId,
          () => apiClient.updateSession(sessionId, { title }),
          'Failed to update session title on server:',
        );
      },

      pinSession: (sessionId: string, pinned: boolean) => {
        set((state) => {
          const sessions = state.sessions.map((s) =>
            s.id === sessionId ? { ...s, pinned } : s
          );
          // Keep pinned sessions first, then by updatedAt desc
          const sorted = [...sessions].sort((a, b) => {
            if ((a as any).pinned && !(b as any).pinned) return -1;
            if (!(a as any).pinned && (b as any).pinned) return 1;
            return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
          });
          return { sessions: sorted } as any;
        });

        // Fire-and-forget: update on server
        sendWhenConfirmed(
          sessionId,
          () => apiClient.updateSession(sessionId, { pinned }),
          'Failed to update session pin on server:',
        );
      },

      reorderSessions: (dragId: string, hoverId: string) => {
        set((state) => {
          const list = [...state.sessions];
          const fromIndex = list.findIndex(s => s.id === dragId);
          const toIndex = list.findIndex(s => s.id === hoverId);
          if (fromIndex === -1 || toIndex === -1) return state;
          const [moved] = list.splice(fromIndex, 1);
          list.splice(toIndex, 0, moved);
          return { sessions: list } as any;
        });
      },

      // Message management
      // STORY-299: every writer takes the id of the chat it writes to. A
      // reply is written to the chat that SENT the request, not to whichever
      // chat is on screen when it arrives; without an id the open chat is
      // meant (the old behaviour, kept for callers with nothing in flight).
      addMessage: (message, sessionId, reply) => {
        const newMessage: Message = {
          ...message,
          id: reply?.id ?? generateUUID(),
          timestamp: new Date(),
        };

        set((state) => {
          const target = targetSession(state, sessionId);
          // Gone (closed, or the store was reset by a logout): write nothing,
          // locally or on the server.
          if (!target) return state;

          // Auto-derive title on first user message if default title
          const deriveTitle = (s: string) => {
            const base = s.replace(/\s+/g, ' ').trim();
            return base.length > 30 ? base.slice(0, 30).trimEnd() + '…' : base || defaultSessionTitle(state.settings.language);
          };

          const shouldAutoTitle =
            isDefaultSessionTitle(target.title) &&
            newMessage.type === 'user' &&
            target.messages.length === 0 &&
            typeof newMessage.content === 'string' && newMessage.content.trim().length > 0;

          const newTitle = shouldAutoTitle ? deriveTitle(String(newMessage.content)) : undefined;

          const updatedSession = {
            ...target,
            messages: [...target.messages, newMessage],
            messageCount: (target.messageCount ?? target.messages.length) + 1,
            updatedAt: new Date(),
            ...(newTitle ? { title: newTitle } : {}),
          } as ChatSession;

          // Persist the message once the server has the session (STORY-376):
          // a message sent before POST /api/sessions answered used to be
          // refused with 404 and was missing from the history after a reload.
          // STORY-299: persisted under the chat it was written to.
          const persistId = target.id;
          // STORY-524: an answer the server already stored is not posted
          // again (were it, the server would answer the stored row).
          if (!reply?.persisted) {
            sendWhenConfirmed(
              persistId,
              () => apiClient.addMessage(persistId, {
                id: newMessage.id,
                type: newMessage.type,
                content: typeof newMessage.content === 'string' ? newMessage.content : String(newMessage.content),
                metadata: newMessage.metadata,
                created_at: newMessage.timestamp.toISOString(),
              }),
              'Failed to persist message to server:',
              () => set({ error: sessionNotSavedError(get().settings.language), errorSessionId: persistId }),
            );
          }

          // Also update title on server if auto-derived
          if (newTitle) {
            sendWhenConfirmed(
              persistId,
              () => apiClient.updateSession(persistId, { title: newTitle }),
              'Failed to update session title on server:',
            );
          }

          return replaceSession(state, updatedSession);
        });
      },

      updateMessage: (messageId: string, updates: Partial<Message>, sessionId?: string) => {
        set((state) => withSession(state, sessionId, (session) => ({
          ...session,
          messages: session.messages.map((m) =>
            m.id === messageId ? { ...m, ...updates } : m
          ),
          updatedAt: new Date(),
        })));
      },

      deleteMessage: (messageId: string, sessionId?: string) => {
        set((state) => withSession(state, sessionId, (session) => ({
          ...session,
          messages: session.messages.filter((m) => m.id !== messageId),
          messageCount: Math.max(
            0,
            (session.messageCount ?? session.messages.length) - 1
          ),
          updatedAt: new Date(),
        })));
      },

      clearMessages: (sessionId?: string) => {
        set((state) => withSession(state, sessionId, (session) => ({
          ...session,
          messages: [],
          messageCount: 0,
          updatedAt: new Date(),
        })));
      },

      clearMessagesAndHistory: async (sessionIdArg?: string) => {
        const target = targetSession(get(), sessionIdArg);
        if (!target) return;
        const sessionId = target.id;

        // Clear local messages first
        get().clearMessages(sessionId);

        // Then clear server-side conversation history + persisted messages
        try {
          // Nothing is stored server-side for a chat whose create failed.
          const generation = _storeGeneration;
          if (!(await whenSessionConfirmed(sessionId)) || generation !== _storeGeneration) return;
          await Promise.all([
            apiClient.clearConversationHistory(sessionId),
            apiClient.clearSessionMessages(sessionId),
          ]);
          console.log(`✅ Server-side history cleared for session: ${sessionId}`);
        } catch (error) {
          console.warn('Failed to clear server-side history:', error);
          // Don't throw - local clear succeeded, server clear is best-effort
        }
      },

      addToolEvents: (events, sessionId) => {
        set((state) => {
          const target = targetSession(state, sessionId);
          if (!target) return state;
          const toolMessage: Message = {
            id: generateUUID(),
            content: 'Tool activity',
            type: 'system',
            timestamp: new Date(),
            metadata: {
              toolCalls: events.map(e => ({ name: e.name, args: e.args, status: (e as any).status, duration_ms: (e as any).duration_ms })),
            },
          };
          const updatedSession = {
            ...target,
            messages: [...target.messages, toolMessage],
            messageCount: (target.messageCount ?? target.messages.length) + 1,
            updatedAt: new Date(),
          };

          // Fire-and-forget: persist tool event message to server
          const persistId = target.id;
          sendWhenConfirmed(
            persistId,
            () => apiClient.addMessage(persistId, {
              id: toolMessage.id,
              type: toolMessage.type,
              content: toolMessage.content,
              metadata: toolMessage.metadata,
              created_at: toolMessage.timestamp.toISOString(),
            }),
            'Failed to persist tool event to server:',
          );

          return replaceSession(state, updatedSession);
        });
      },

      refreshSessionStats: async (sessionId) => {
        if (!sessionId) return;
        const requestId = (_sessionStatsRequests.get(sessionId) ?? 0) + 1;
        _sessionStatsRequests.set(sessionId, requestId);
        try {
          // STORY-376: a chat whose create has not answered has no usage yet,
          // and the server 404s an id it has not created. Ask once it has.
          const generation = _storeGeneration;
          if (!(await whenSessionConfirmed(sessionId)) || generation !== _storeGeneration) return;
          const data = await apiClient.getSessionCost(sessionId);
          // Drop a response overtaken by a newer refresh of the same session.
          if (_sessionStatsRequests.get(sessionId) !== requestId) return;
          set((state) => ({
            sessionStats: { ...state.sessionStats, [sessionId]: data },
            sessionStatsRevision: state.sessionStatsRevision + 1,
          }));
        } catch {
          // Keep the last snapshot; still let the other counters refetch.
          set((state) => ({ sessionStatsRevision: state.sessionStatsRevision + 1 }));
        }
      },

      addSessionTokenUsage: (usage, sessionId) => {
        set((state) => withSession(state, sessionId, (session) => {
          const currentUsage = session.tokenUsage || {
            promptTokens: 0,
            completionTokens: 0,
            totalTokens: 0,
            estimatedCostUsd: 0,
            requestCount: 0,
          };

          const updatedUsage: SessionTokenUsage = {
            promptTokens: currentUsage.promptTokens + (usage.prompt_tokens || 0),
            completionTokens: currentUsage.completionTokens + (usage.completion_tokens || 0),
            totalTokens: currentUsage.totalTokens + (usage.total_tokens || 0),
            estimatedCostUsd: currentUsage.estimatedCostUsd + (usage.estimated_cost_usd || 0),
            requestCount: currentUsage.requestCount + 1,
          };

          return { ...session, tokenUsage: updatedUsage };
        }));
      },

      // ---------------------------------------------------------------
      // STORY-299: chat turns, at most one in flight per session
      // ---------------------------------------------------------------

      beginTurn: (sessionId, retry, turnId, serverSave) => {
        // A reattached turn (STORY-524) has no controller; it is still the
        // chat's one turn in flight.
        if (!sessionId || _chatControllers.has(sessionId) || get().pendingBySession[sessionId]) return null;
        // A chat that is gone (closed, or the store was reset on logout) gets
        // no turn: a timer scheduled for it earlier (the auto-retry) must not
        // post /chat for a chat the user closed, or for the user who left.
        // "Gone" is what the writes use (targetSession), so a turn is refused
        // exactly when its reply could not be written anyway.
        if (!targetSession(get(), sessionId)) return null;
        const controller = new AbortController();
        _chatControllers.set(sessionId, controller);
        set((state) => ({
          pendingBySession: {
            ...state.pendingBySession,
            [sessionId]: {
              since: Date.now(), ...(retry ? { retry } : {}), ...(turnId ? { turnId } : {}),
              ...(serverSave !== undefined ? { serverSave } : {}),
            },
          },
        }));
        return controller.signal;
      },

      endTurn: (sessionId, signal) => {
        // Only the turn that owns the signal may end itself: after a Stop and
        // a new send, the stopped turn's late `finally` must not clear the
        // new one.
        if (_chatControllers.get(sessionId)?.signal !== signal) return;
        _chatControllers.delete(sessionId);
        set((state) => ({ pendingBySession: withoutKey(state.pendingBySession, sessionId) }));
      },

      abortTurn: (sessionId) => {
        const pending = get().pendingBySession[sessionId];
        const turnId = pending?.turnId;
        // A turn the server does not store (serverSave false: auto-retry,
        // execute-plan, clear-and-retry, tool interpretation) has no answer
        // to show after a lost race — this page aborted its own save. A
        // reattached turn (unknown) is polled; the poll reads `saved`.
        const answerMayBeStored = pending?.serverSave !== false;
        stopReattachPoll(sessionId);
        abortTurnOf(sessionId);
        set((state) => (state.pendingBySession[sessionId]
          ? { pendingBySession: withoutKey(state.pendingBySession, sessionId) }
          : state));
        if (!turnId) return;
        const generation = _storeGeneration;
        void sendTurnCancel(turnId, 'stop').then((status) => {
          // Stop raced the answer: it exists (and was paid for), so show it
          // once stored rather than pretend nothing came back.
          if (status === 'already_done' && answerMayBeStored && generation === _storeGeneration
            && targetSession(get(), sessionId) && !get().pendingBySession[sessionId]) {
            reattachTurn(sessionId, { turn_id: turnId, since: new Date().toISOString() });
          }
        });
      },

      // MCP Server management
      addMCPServer: (server) => {
        const newServer: MCPServer = {
          ...server,
          id: generateUUID(),
        };
        
        set((state) => ({
          mcpServers: [...state.mcpServers, newServer],
        }));
      },

      updateMCPServer: (serverId: string, updates: Partial<MCPServer>) => {
        set((state) => ({
          mcpServers: state.mcpServers.map((s) =>
            s.id === serverId ? { ...s, ...updates } : s
          ),
        }));
      },

      deleteMCPServer: (serverId: string) => {
        set((state) => ({
          mcpServers: state.mcpServers.filter((s) => s.id !== serverId),
        }));
      },

      toggleMCPServer: (serverId: string) => {
        set((state) => ({
          mcpServers: state.mcpServers.map((s) =>
            s.id === serverId ? { ...s, enabled: !s.enabled } : s
          ),
        }));
      },

      toggleFavoriteTool: (serverIdOrName: string, toolName: string) => {
        set((state) => {
          const servers = state.mcpServers.map((s) => {
            const isMatch = s.id === serverIdOrName || s.name === serverIdOrName;
            if (!isMatch) return s;
            const tools = (s.tools || []).map((t: any) =>
              t.name === toolName ? { ...t, favorite: !t.favorite } : t
            );
            return { ...s, tools } as any;
          });
          return { mcpServers: servers };
        });
      },

      // LLM Provider management
      addLLMProvider: (provider) => {
        const id = makeLLMProviderId(provider.type, provider.model || '');
        // Same type+model already listed → update in place (no duplicate row)
        const existing = get().llmProviders.find((p) => p.id === id);
        if (existing) {
          set((state) => ({
            llmProviders: state.llmProviders.map((p) =>
              p.id === id ? { ...p, ...provider, id } : p
            ),
          }));
          return;
        }

        set((state) => ({
          llmProviders: [...state.llmProviders, { ...provider, id }],
        }));
      },

      updateLLMProvider: (providerId: string, updates: Partial<LLMProvider>) => {
        set((state) => {
          let selectedId = state.settings.selectedLLMProvider;
          let editedIdx = -1;
          const llmProviders = state.llmProviders.map((p, idx) => {
            if (p.id !== providerId) return p;
            editedIdx = idx;
            const merged = { ...p, ...updates };
            const newId = makeLLMProviderId(merged.type, merged.model || '');
            if (selectedId === p.id && newId !== p.id) {
              selectedId = newId;
            }
            return { ...merged, id: newId };
          });
          // Collapse accidental duplicates after model rename — on a collision,
          // keep the edited row (by original index) rather than whichever copy
          // happens to appear first, so the just-applied edit is never silently
          // discarded in favor of a stale duplicate.
          const firstIdxForId = new Map<string, number>();
          llmProviders.forEach((p, idx) => {
            const existingIdx = firstIdxForId.get(p.id);
            if (existingIdx === undefined || idx === editedIdx) {
              firstIdxForId.set(p.id, idx);
            }
          });
          const deduped = llmProviders.filter((p, idx) => firstIdxForId.get(p.id) === idx);
          return {
            llmProviders: deduped,
            settings:
              selectedId !== state.settings.selectedLLMProvider
                ? { ...state.settings, selectedLLMProvider: selectedId }
                : state.settings,
          };
        });
      },

      deleteLLMProvider: (providerId: string) => {
        set((state) => ({
          llmProviders: state.llmProviders.filter((p) => p.id !== providerId),
        }));
      },

      setActiveLLMProvider: (providerId: string) => {
        set((state) => {
          const provider = state.llmProviders.find(p => p.id === providerId);
          if (!provider) return state;
          
          // Toggle the enabled state (not mutually exclusive - multiple can be enabled)
          const updatedProviders = state.llmProviders.map((p) => 
            p.id === providerId ? { ...p, enabled: !p.enabled } : p
          );
          
          // If in single mode and activating, also set as selected provider
          // If deactivating in single mode, clear selection if it was the selected one
          let updatedSettings = state.settings;
          if (state.settings.llmMode === 'single') {
            if (!provider.enabled) {
              // Activating - set as selected
              updatedSettings = { ...state.settings, selectedLLMProvider: providerId };
            } else if (state.settings.selectedLLMProvider === providerId) {
              // Deactivating the selected provider - clear selection
              updatedSettings = { ...state.settings, selectedLLMProvider: undefined };
            }
          }
          
          return {
            llmProviders: updatedProviders,
            settings: updatedSettings,
          };
        });
      },

      /**
       * Pull providers that the backend already initialized (Vault / .env)
       * into the UI list. Fixes cross-device: credentials live on the server,
       * but the dropdown used to read only this browser's localStorage.
       * Match key is type+model (stable id), so several Claude/Ollama models
       * can appear side by side.
       */
      syncLLMProvidersFromBackend: async () => {
        try {
          const result = await apiClient.getAvailableProviders();
          // status !== 'success' means the LLM manager wasn't ready yet (a
          // transient boot-time state, not "nothing is configured") — bail
          // out without touching local state. An empty `providers` array
          // WITH status 'success' is a real signal (nothing configured /
          // everything was deleted) and must still run the reconciliation
          // below, or a provider deleted on another device — including the
          // last one — would never disappear here.
          if (result?.status !== 'success') return;
          const backendProviders = result.providers || [];

          let remappedSelected: string | undefined | null = null; // null = no change

          set((state) => {
            // First upgrade any legacy local ids → type-model
            const migrated = migrateProviderList(
              state.llmProviders,
              state.settings.selectedLLMProvider
            );
            let providers = migrated.providers;
            let selectedId = migrated.selectedId;

            // Count backend providers per type up front so a lone local stub
            // (e.g. the hardcoded default GigaChat placeholder, which guesses
            // a model name before the real backend config is known) can be
            // upgraded in place instead of duplicated when its model name
            // doesn't match. Only do this when it's unambiguous — exactly one
            // backend provider of that type and exactly one still-unmatched
            // local row of the same type — so genuinely distinct models of
            // the same provider (multiple Ollama models, etc.) still end up
            // as separate rows.
            const typeCounts = new Map<string, number>();
            for (const bp of backendProviders) {
              const rawType = String(bp.provider || bp.provider_name || '');
              if (!rawType) continue;
              const t = mapBackendProviderType(rawType);
              typeCounts.set(t, (typeCounts.get(t) || 0) + 1);
            }

            const matchedIdxs = new Set<number>();

            for (const bp of backendProviders) {
              const rawType = String(bp.provider || bp.provider_name || '');
              if (!rawType) continue;

              // Backend reports `enabled` (admin on/off toggle) separately
              // from `is_available` (live health probe) — a registered-but-
              // disabled provider must still show up in the UI (as "off"),
              // not disappear from sync entirely.
              const bpEnabled = bp.enabled !== undefined ? !!bp.enabled : bp.is_available !== false;
              // STORY-520: what real calls saw (the Failing badge reads it).
              const bpHealth = parseProviderHealth(bp.health);

              const providerType = mapBackendProviderType(rawType);
              // Prefer explicit model; for keys like "ollama-qwen2.5:32b" derive from name
              let model = String(bp.model || '').trim();
              if (!model && providerType !== 'custom') {
                const prefix = `${providerType}-`;
                const pname = String(bp.provider_name || bp.id || '');
                if (pname.startsWith(prefix)) {
                  model = pname.slice(prefix.length);
                }
              }
              const stableId =
                providerType === 'custom' && !model
                  ? rawType
                  : makeLLMProviderId(providerType, model);

              let existingIdx = providers.findIndex(
                (p, idx) =>
                  !matchedIdxs.has(idx) &&
                  (p.id === stableId || (p.type === providerType && (p.model || '') === model))
              );

              if (existingIdx < 0 && typeCounts.get(providerType) === 1) {
                const sameTypeUnmatched = providers.reduce<number[]>((acc, p, idx) => {
                  if (!matchedIdxs.has(idx) && p.type === providerType) acc.push(idx);
                  return acc;
                }, []);
                if (sameTypeUnmatched.length === 1) {
                  existingIdx = sameTypeUnmatched[0];
                }
              }

              if (existingIdx >= 0) {
                matchedIdxs.add(existingIdx);
                const old = providers[existingIdx];
                providers[existingIdx] = {
                  ...old,
                  id: stableId,
                  name: bp.name || old.name,
                  model: model || old.model,
                  endpoint: bp.base_url || old.endpoint,
                  enabled: bpEnabled,
                  health: bpHealth,
                };
                if (selectedId === old.id && old.id !== stableId) {
                  selectedId = stableId;
                }
              } else {
                providers.push({
                  id: stableId,
                  name: bp.name || `${rawType} (auto-detected)`,
                  type: providerType,
                  model: model || '',
                  endpoint: bp.base_url || '',
                  apiKey: '',
                  enabled: bpEnabled,
                  health: bpHealth,
                  settings: { maxTokens: 128000, temperature: 0.7 },
                });
                matchedIdxs.add(providers.length - 1);
              }
            }

            // Reconcile: drop local rows the backend no longer reports (e.g.
            // deleted via the trash icon on another device — or on this one
            // in a session that never got a chance to sync). The backend
            // response here is authoritative once status is 'success', so
            // anything not matched this round is stale, not "still pending".
            providers = providers.filter((_, idx) => matchedIdxs.has(idx));

            // Orphan selection: the id no longer matches any provider. Only
            // auto-upgrade the legacy case where the selection was a bare
            // provider type (very old versions stored type-only ids) — pick
            // that type's first available model. Anything else (a specific
            // type-model id whose model is no longer registered, or a stale
            // UUID) is cleared rather than silently swapped for an unrelated
            // model the user never chose.
            if (selectedId && !providers.some((p) => p.id === selectedId)) {
              const bareTypeMatch = providers.find((p) => p.type === selectedId);
              selectedId = bareTypeMatch?.id;
            }

            remappedSelected =
              selectedId !== state.settings.selectedLLMProvider ? selectedId : null;

            return {
              llmProviders: providers,
              ...(remappedSelected !== null
                ? { settings: { ...state.settings, selectedLLMProvider: selectedId } }
                : {}),
            };
          });

          // Persist remapped selection so other devices get a stable id next time
          if (remappedSelected !== null) {
            const merged = {
              ...get().settings,
              selectedLLMProvider: remappedSelected,
            };
            apiClient.saveSettings(merged as Record<string, unknown>).catch((err) => {
              console.warn('Failed to persist remapped LLM selection:', err);
            });
          }
        } catch (err) {
          console.warn('Failed to sync LLM providers from backend:', err);
        }
      },

      // Settings
      updateSettings: (settings: Partial<AppSettings>) => {
        set((state) => {
          const merged = { ...state.settings, ...settings };
          // Fire-and-forget: persist to server
          apiClient.saveSettings(merged as Record<string, any>).catch((err) => {
            console.warn('Failed to persist settings to server:', err);
          });
          return { settings: merged };
        });
      },

      loadSettingsFromServer: async () => {
        try {
          const { settings: serverSettings } = await apiClient.getSettings();
          if (serverSettings && Object.keys(serverSettings).length > 0) {
            // Server wins — merge server settings over defaults, then over current
            set((state) => ({
              settings: { ...defaultSettings, ...state.settings, ...serverSettings },
            }));
          }
        } catch (err) {
          console.warn('Failed to load settings from server:', err);
        }
      },

      // UI state
      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setError: (error: string | null, sessionId?: string) => set({ error, errorSessionId: error ? sessionId ?? null : null }),
      
      // Cross-component refresh triggers
      triggerVaultRefresh: () => set((state) => ({ vaultRefreshTrigger: state.vaultRefreshTrigger + 1 })),
    }),
    {
      name: 'mcp-chat-storage-v3', // key name kept for continuity; version below drives migrate
      version: 4, // v4: stable ids = type-model + sync providers from backend
      storage: createJSONStorage(() => safeLocalStorage),
      migrate: (persisted: any) => {
        // Normalize old/corrupt data to avoid runtime crashes
        try {
          const state = persisted?.state ?? persisted ?? {};
          const rawProviders = Array.isArray(state.llmProviders)
            ? state.llmProviders
            : [defaultLLMProvider];
          const settings = { ...(state.settings ?? defaultSettings) };
          const { providers, selectedId } = migrateProviderList(
            rawProviders,
            settings.selectedLLMProvider
          );
          settings.selectedLLMProvider = selectedId;
          return {
            // Sessions are now server-side — don't restore from localStorage,
            // only remember which one was open
            mcpServers: Array.isArray(state.mcpServers) ? state.mcpServers : defaultMCPServers,
            llmProviders: providers,
            settings,
            lastActiveSessionId: typeof state.lastActiveSessionId === 'string' ? state.lastActiveSessionId : null,
          } as any;
        } catch {
          return {
            mcpServers: defaultMCPServers,
            llmProviders: [defaultLLMProvider],
            settings: defaultSettings,
          } as any;
        }
      },
      partialize: (state) => ({
        // Sessions are now stored server-side — don't persist to localStorage;
        // only the id of the open session, so a reload can re-select it
        mcpServers: state.mcpServers,
        llmProviders: state.llmProviders,
        settings: state.settings,
        lastActiveSessionId: state.lastActiveSessionId,
      }),
    }
  )
);

/** STORY-299: true while the chat `sessionId` has a turn in flight. */
export function useIsSessionBusy(sessionId: string | null | undefined): boolean {
  return useAppStore((state) => Boolean(sessionId && state.pendingBySession[sessionId]));
}

/** Non-hook form of useIsSessionBusy, for callbacks and timers. */
export function isSessionBusy(sessionId: string | null | undefined): boolean {
  return Boolean(sessionId && useAppStore.getState().pendingBySession[sessionId]);
}

/**
 * STORY-524: show a chat's turn that is running on the server but was not
 * sent by this page (a reload while it waited, or a Stop that lost the race
 * with the answer). The chat is busy — the existing indicator and Stop — and
 * the turn is polled; once done, the chat's messages are reloaded from the
 * server, which stored the answer. A turn the server no longer knows (it
 * restarted) just ends the busy state, as before this story.
 */
export function reattachTurn(sessionId: string, turn: { turn_id: string; since?: string }): void {
  const state = useAppStore.getState();
  if (state.pendingBySession[sessionId] || !targetSession(state, sessionId)) return;
  stopReattachPoll(sessionId);
  const turnId = turn.turn_id;
  const since = turn.since ? Date.parse(turn.since) : NaN;
  useAppStore.setState((s) => ({
    pendingBySession: {
      ...s.pendingBySession,
      [sessionId]: { since: Number.isNaN(since) ? Date.now() : since, turnId, reattached: true },
    },
  }));
  const generation = _storeGeneration;
  let inFlight = false;
  const check = async () => {
    if (inFlight) return;
    inFlight = true;
    try {
      await pollReattached(sessionId, turnId, generation);
    } finally {
      inFlight = false;
    }
  };
  const onVisible = () => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') void check();
  };
  const timer = setInterval(() => void check(), REATTACH_POLL_MS);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
  _reattachPolls.set(sessionId, { turnId, timer, onVisible });
  void check();
}

/** Still this reattached turn of this chat, for this user (no logout since)? */
function isReattached(sessionId: string, turnId: string, generation: number): boolean {
  const pending = useAppStore.getState().pendingBySession[sessionId];
  return generation === _storeGeneration && Boolean(pending?.reattached) && pending?.turnId === turnId;
}

async function pollReattached(sessionId: string, turnId: string, generation: number): Promise<void> {
  if (!isReattached(sessionId, turnId, generation)) {
    if (_reattachPolls.get(sessionId)?.turnId === turnId) stopReattachPoll(sessionId);
    return;
  }
  let state: string;
  let saved = true;
  try {
    const turn = await apiClient.getTurn(turnId);
    state = turn.state;
    saved = turn.saved !== false;
  } catch (err) {
    // 404: the server no longer knows the turn (restarted, or long gone).
    if (err instanceof ApiError && err.status === 404) state = 'gone';
    else return; // a network blip: ask again on the next tick
  }
  if (state === 'running' || state === 'finishing') return;
  if (!isReattached(sessionId, turnId, generation)) return;
  stopReattachPoll(sessionId);
  // done but not stored (the server save failed, or the turn was not one
  // the server stores): there is no answer to load.
  if (state === 'done' && saved) {
    try {
      const detail = await apiClient.getSession(sessionId);
      if (!isReattached(sessionId, turnId, generation)) return;
      const messages = messagesFromHistory(detail.messages);
      useAppStore.setState((s) => withSession(s, sessionId, (session) => ({
        ...session, messages, messageCount: messages.length,
      })));
    } catch (err) {
      console.warn('Failed to load the answer of a reattached turn:', err);
    }
  }
  if (!isReattached(sessionId, turnId, generation)) return;
  useAppStore.setState((s) => ({ pendingBySession: withoutKey(s.pendingBySession, sessionId) }));
  void useAppStore.getState().refreshSessionStats(sessionId);
}

/**
 * STORY-511: logout stops this tab's turns on the server. Called by
 * authStore.logout BEFORE the token is dropped (the request needs it). Turns
 * of the same user in other tabs are not touched. Never throws.
 */
export function cancelTurnsForLogout(): void {
  const turnIds = Object.values(useAppStore.getState().pendingBySession)
    .map((p) => p.turnId)
    .filter((id): id is string => Boolean(id));
  if (!turnIds.length) return;
  // Called synchronously, not from a microtask: the request's headers are
  // built in this call, and the caller drops the token right after it.
  try {
    void apiClient.cancelTurns(turnIds)
      .catch((err) => console.warn('Failed to cancel turns on logout:', err));
  } catch (err) {
    console.warn('Failed to cancel turns on logout:', err);
  }
}

/**
 * Reset the store to factory defaults (used on logout so the next user starts
 * clean).
 */
export function resetAppStore() {
  _loadedSessionIds.clear();
  _sessionCreates.clear();
  _storeGeneration += 1;
  // STORY-299: replies still in flight belong to the user who logged out.
  abortAllTurns();
  useAppStore.setState({
    currentSession: null,
    sessions: [],
    mcpServers: useAppStore.getState().mcpServers, // keep local prefs
    llmProviders: useAppStore.getState().llmProviders, // keep local prefs
    settings: useAppStore.getState().settings, // keep local prefs
    isLoading: false,
    pendingBySession: {},
    historyLoadingIds: [],
    lastActiveSessionId: null,
    error: null,
    errorSessionId: null,
    sessionStats: {},
    sessionStatsRevision: 0,
  });
  _sessionStatsRequests.clear();
}

/** Sessions ready for an export, and how many unsaved empty chats were left out. */
export interface ExportHydration {
  sessions: ChatSession[];
  /** Empty chats whose POST /api/sessions failed: nothing to export, nothing on the server. */
  skippedUnsaved: number;
}

/**
 * Fetch the messages of every chat that has none loaded, for an export.
 * Bounded: one GET per session with no cap opens hundreds of concurrent
 * requests on a heavy account. A single failure still aborts the export — a
 * backup quietly missing one chat's messages is the very bug this prevents.
 *
 * STORY-376: a chat whose create is in flight is waited for (a GET before it
 * would 404 and abort the whole export); an empty chat whose create failed
 * has nothing to fetch and is skipped and counted instead.
 */
export async function hydrateSessionsForExport(sessions: ChatSession[]): Promise<ExportHydration> {
  let skippedUnsaved = 0;
  const outcomes = await mapSettledWithLimit(sessions, SERVER_FANOUT_LIMIT, async (session): Promise<ChatSession | null> => {
    if (session.messages.length > 0) {
      return session;
    }
    if (!(await whenSessionConfirmed(session.id))) {
      skippedUnsaved += 1;
      return null;
    }
    const detail = await apiClient.getSession(session.id);
    const messages: Message[] = detail.messages.map((m) => ({
      id: m.id,
      type: m.type as 'user' | 'assistant' | 'system',
      content: m.content,
      timestamp: new Date(m.created_at),
      metadata: m.metadata || undefined,
    }));
    return { ...session, messages };
  });

  const failure = outcomes.find((o) => o.status === 'rejected');
  if (failure && failure.status === 'rejected') {
    throw failure.reason;
  }
  const hydrated = outcomes
    .map((o) => (o as PromiseFulfilledResult<ChatSession | null>).value)
    .filter((x): x is ChatSession => x !== null);
  return { sessions: hydrated, skippedUnsaved };
}

/** What "Clear all data" actually managed to wipe on the server. */
export interface ClearAllResult {
  /** Sessions the wipe was asked to delete. */
  total: number;
  /** Sessions the server confirmed deleted. */
  deleted: number;
  /** Sessions whose DELETE failed — these still exist server-side. */
  failed: number;
  /** True when the settings reset did not reach the server. */
  settingsFailed: boolean;
}

/**
 * Wipe chat sessions (server), settings (server), and local prefs, matching
 * the Import/Export "Clear all data" action.
 *
 * Never throws for a per-session failure. A destructive fan-out that gives up
 * on the first rejection is the worst of the three possible outcomes: some
 * chats are already gone from the server, the local store still lists all of
 * them, and the caller reports "an error occurred" — so the user believes
 * nothing happened and is left with sidebar rows that 404 when clicked. Every
 * DELETE is settled, the local state is wiped regardless so the UI matches
 * whatever the server now holds, and the partial outcome is returned for the
 * caller to show.
 */
export async function clearAllAppData(): Promise<ClearAllResult> {
  const { sessions } = useAppStore.getState();

  const outcomes = await mapSettledWithLimit(sessions, SERVER_FANOUT_LIMIT, (s) =>
    apiClient.deleteServerSession(s.id)
  );
  const failed = outcomes.filter((o) => o.status === 'rejected').length;

  // Best-effort too: a failed settings reset must not abort the local wipe.
  let settingsFailed = false;
  try {
    await apiClient.saveSettings({ ...defaultSettings });
  } catch (err) {
    console.error('Clear all data: settings reset failed', err);
    settingsFailed = true;
  }

  _loadedSessionIds.clear();
  abortAllTurns(); // STORY-299: every chat is gone; so are their replies
  useAppStore.setState({
    currentSession: null,
    sessions: [],
    mcpServers: defaultMCPServers,
    llmProviders: [defaultLLMProvider],
    settings: { ...defaultSettings },
    isLoading: false,
    pendingBySession: {},
    error: null,
  });

  try {
    localStorage.removeItem('mcp-chat-storage-v3');
    localStorage.removeItem('mcp-chat-storage'); // legacy key used by the old clear button
  } catch {
    // ignore storage errors
  }

  return {
    total: sessions.length,
    deleted: sessions.length - failed,
    failed,
    settingsFailed,
  };
}

function toIsoTimestamp(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string' && value.trim()) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
  }
  return undefined;
}

/**
 * What an import will accept, checked before the first request goes out.
 *
 * Import issues one createSession per chat plus one addMessage per message,
 * strictly sequentially. A 200-chat x 200-message backup is ~40 000 round
 * trips — twenty-odd minutes behind a disabled button. Refusing that up front,
 * with the limit named, beats starting it.
 */
export const IMPORT_LIMITS = {
  /** Checked by the caller against the File, before the bytes are even read. */
  maxBytes: 20 * 1024 * 1024,
  maxSessions: 500,
  maxMessages: 20000,
};

/** Why an import stopped. The three cases need three different things said. */
export type ImportFailureKind =
  /** The file is not a backup we understand — the file really is at fault. */
  | 'format'
  /** The file is well-formed but bigger than IMPORT_LIMITS. Nothing was sent. */
  | 'limit'
  /** The server or the connection failed partway. The file is fine. */
  | 'transport';

export class ImportError extends Error {
  kind: ImportFailureKind;
  /** For 'limit': what was exceeded, with the actual and allowed numbers. */
  limit?: { of: 'sessions' | 'messages'; actual: number; allowed: number };
  /** For 'transport': how far the import got before it stopped. */
  progress?: { sessionsImported: number; sessionsTotal: number };

  constructor(kind: ImportFailureKind, message: string) {
    super(message);
    this.name = 'ImportError';
    this.kind = kind;
  }
}

export interface ImportResult {
  sessionsImported: number;
  sessionsTotal: number;
  /**
   * Settings keys from the file that were NOT applied — unknown to AppSettings
   * or of the wrong type. Never sent to the server; the caller says so.
   */
  settingsRejected: string[];
}

/**
 * Merge an exported backup into the current user's data (sessions on server,
 * MCP/LLM/settings locally + settings on server).
 *
 * Throws only ImportError. A caller that reports every failure as "invalid
 * input" lies about a dropped connection, and a caller that does not reload
 * after a partial import leaves the chats that did land invisible.
 */
export async function importAppData(
  data: {
    sessions?: unknown;
    mcpServers?: unknown;
    llmProviders?: unknown;
    settings?: unknown;
  },
  onProgress?: (sessionsImported: number, sessionsTotal: number) => void
): Promise<ImportResult> {
  const hasSessions = Array.isArray(data.sessions);
  const hasMcp = Array.isArray(data.mcpServers);
  const hasLlm = Array.isArray(data.llmProviders);
  const hasSettings = data.settings != null && typeof data.settings === 'object' && !Array.isArray(data.settings);

  if (!hasSessions && !hasMcp && !hasLlm && !hasSettings) {
    throw new ImportError('format', 'Invalid file format');
  }

  // Bound first: nothing has been sent yet, so an oversized backup is refused
  // with the limit named rather than half-applied.
  const rawSessions = hasSessions ? (data.sessions as any[]) : [];
  const sessionsTotal = rawSessions.length;
  if (sessionsTotal > IMPORT_LIMITS.maxSessions) {
    const err = new ImportError(
      'limit',
      `Backup has ${sessionsTotal} sessions, limit is ${IMPORT_LIMITS.maxSessions}`
    );
    err.limit = { of: 'sessions', actual: sessionsTotal, allowed: IMPORT_LIMITS.maxSessions };
    throw err;
  }
  const messagesTotal = rawSessions.reduce(
    (sum, raw) => sum + (raw && Array.isArray(raw.messages) ? raw.messages.length : 0),
    0
  );
  if (messagesTotal > IMPORT_LIMITS.maxMessages) {
    const err = new ImportError(
      'limit',
      `Backup has ${messagesTotal} messages, limit is ${IMPORT_LIMITS.maxMessages}`
    );
    err.limit = { of: 'messages', actual: messagesTotal, allowed: IMPORT_LIMITS.maxMessages };
    throw err;
  }

  if (hasMcp) {
    const incoming = (data.mcpServers as MCPServer[]).filter((s) => s && typeof s.id === 'string');
    useAppStore.setState((state) => {
      const byId = new Map(state.mcpServers.map((s) => [s.id, s]));
      for (const server of incoming) {
        byId.set(server.id, { ...byId.get(server.id), ...server });
      }
      return { mcpServers: Array.from(byId.values()) };
    });
  }

  if (hasLlm) {
    const incoming = (data.llmProviders as LLMProvider[]).filter((p) => p && typeof p.id === 'string');
    useAppStore.setState((state) => {
      const byId = new Map(state.llmProviders.map((p) => [p.id, p]));
      for (const provider of incoming) {
        byId.set(provider.id, { ...byId.get(provider.id), ...provider });
      }
      return { llmProviders: Array.from(byId.values()) };
    });
  }

  let sessionsImported = 0;
  /** A server/network failure, re-thrown with how far the import had got. */
  const asTransportError = (err: unknown) => {
    const wrapped = new ImportError(
      'transport',
      err instanceof Error ? err.message : String(err)
    );
    wrapped.progress = { sessionsImported, sessionsTotal };
    return wrapped;
  };

  // Only known AppSettings keys with valid values leave the browser
  // (STORY-331). The current settings are kept as they are — they hold keys
  // other endpoints own (cluster_routing, shared settings) — but nothing the
  // FILE carries beyond AppSettings is merged or sent.
  const { accepted: incomingSettings, rejected: settingsRejected } = hasSettings
    ? sanitizeImportedSettings(data.settings)
    : { accepted: {}, rejected: [] as string[] };

  if (Object.keys(incomingSettings).length > 0) {
    const merged = { ...useAppStore.getState().settings, ...incomingSettings };
    try {
      await apiClient.saveSettings(merged as Record<string, unknown>);
    } catch (err) {
      throw asTransportError(err);
    }
    useAppStore.setState({ settings: merged });
  }

  if (hasSessions) {
    onProgress?.(0, sessionsTotal);

    try {
      for (const raw of rawSessions) {
        // Junk entries are skipped, and stay uncounted: "imported X of Y" must
        // mean X chats really are on the server now.
        if (!raw || typeof raw !== 'object') continue;

        let sessionId = typeof raw.id === 'string' && raw.id ? raw.id : generateUUID();
        const title = typeof raw.title === 'string' && raw.title.trim() ? raw.title : 'Imported chat';
        let remintMessageIds = false;

        try {
          await apiClient.createSession(sessionId, title);
        } catch (err) {
          if (err instanceof ApiError && err.status === 409) {
            sessionId = generateUUID();
            remintMessageIds = true;
            await apiClient.createSession(sessionId, title);
          } else {
            throw err;
          }
        }

        if (raw.pinned) {
          await apiClient.updateSession(sessionId, { pinned: true });
        }

        const messages = Array.isArray(raw.messages) ? raw.messages : [];
        for (const m of messages) {
          if (!m || typeof m !== 'object') continue;
          const type = m.type === 'user' || m.type === 'assistant' || m.type === 'system' ? m.type : null;
          if (!type) continue;

          const content =
            typeof m.content === 'string' ? m.content : m.content == null ? '' : String(m.content);
          let messageId =
            !remintMessageIds && typeof m.id === 'string' && m.id.length > 0 && m.id.length <= 36
              ? m.id
              : generateUUID();

          try {
            await apiClient.addMessage(sessionId, {
              id: messageId,
              type,
              content,
              metadata: m.metadata || undefined,
              created_at: toIsoTimestamp(m.timestamp ?? m.created_at),
            });
          } catch (err) {
            if (err instanceof ApiError && err.status === 409) {
              messageId = generateUUID();
              await apiClient.addMessage(sessionId, {
                id: messageId,
                type,
                content,
                metadata: m.metadata || undefined,
                created_at: toIsoTimestamp(m.timestamp ?? m.created_at),
              });
            } else {
              throw err;
            }
          }
        }

        sessionsImported += 1;
        onProgress?.(sessionsImported, sessionsTotal);
      }
    } catch (err) {
      throw err instanceof ImportError ? err : asTransportError(err);
    }
  }

  return { sessionsImported, sessionsTotal, settingsRejected };
}
